import { asyncActionCreator } from 'src/utils/loadingUtils';
import {
  getPaymentFailures,
  cancelPayment,
  editPaymentDueDate,
  sendToBillPayment,
  downloadExportedAutomaticDebits,
  generateAutomaticDebitFormats,
  createAutomaticDebit,
  reactivateAutomaticDebit,
  editAutomaticDebitAmount
} from 'src/services/paymentService';
import { SnackBarVariants } from 'src/utils/constants/common';
import { showMessage } from './snackBarActions';
import { selectPaymentQueryOptions } from '../selectors/paymentSelectors';

export const LOAD_PAYMENTS = asyncActionCreator('LOAD_PAYMENTS');
export const SAVE_PAYMENTS_OPTIONS = 'SAVE_PAYMENTS_OPTIONS';
export const CLEAR_PAYMENTS_OPTIONS = 'CLEAR_PAYMENT_OPTIONS';
export const LOAD_PAYMENTS_LIST = asyncActionCreator('LOAD_PAYMENTS_LIST');
export const CANCEL_PAYMENT = asyncActionCreator('CANCEL_PAYMENT');
export const CLEAR_PAYMENT = 'CLEAR_PAYMENT';
export const EDIT_PAYMENT_DUE_DATE = asyncActionCreator(
  'EDIT_PAYMENT_DUE_DATE'
);
export const SEND_PAYMENT_TO_REBILL = asyncActionCreator(
  'SEND_PAYMENT_TO_REBILL'
);
export const CLEAR_PAYMENT_OPTIONS = 'CLEAR_PAYMENT_OPTIONS';
export const GENERATE_AUTOMATIC_DEBIT_FORMATS = asyncActionCreator(
  'GENERATE_AUTOMATIC_DEBIT_FORMATS'
);
export const DOWNLOAD_AUTOMATIC_DEBITS = asyncActionCreator(
  'DOWNLOAD_AUTOMATIC_DEBITS'
);
export const CREATE_AUTOMATIC_DEBIT = asyncActionCreator(
  'CREATE_AUTOMATIC_DEBIT'
);
export const REACTIVATE_AUTOMATIC_DEBIT = asyncActionCreator(
  'REACTIVATE_AUTOMATIC_DEBIT'
);
export const EDIT_AUTOMATIC_DEBIT_AMOUNT = asyncActionCreator(
  'EDIT_AUTOMATIC_DEBIT_AMOUNT'
);

function getOptions(getState, sendLimit = true) {
  const queryOpts = selectPaymentQueryOptions(getState());
  const reqOptions = {};

  if (queryOpts.page > 0) {
    reqOptions.offset =
      queryOpts.page * Number(process.env.REACT_APP_TABLE_SIZE);
  }
  if (queryOpts.clientName) {
    reqOptions.clientName = queryOpts.clientName;
  }
  if (sendLimit) {
    reqOptions.limit = queryOpts.limit;
  }
  if (queryOpts.status) {
    reqOptions.status = queryOpts.status;
  }
  if (
    queryOpts.dueDateRange &&
    queryOpts.dueDateRange.startDate &&
    queryOpts.dueDateRange.endDate
  ) {
    reqOptions.dueDateRange = queryOpts.dueDateRange;
  }
  return reqOptions;
}

export const clearPaymentQueryOptions = () => dispatch => {
  dispatch({ type: CLEAR_PAYMENTS_OPTIONS });
};

export const setQueryOptions = options => dispatch => {
  dispatch({
    type: SAVE_PAYMENTS_OPTIONS,
    options
  });
};

const loadPayments = options => async (dispatch, getState) => {
  try {
    dispatch({
      type: LOAD_PAYMENTS_LIST.start
    });

    dispatch({
      type: SAVE_PAYMENTS_OPTIONS,
      options
    });

    const reqOptions = {
      ...getOptions(getState),
      ...options
    };

    const data = await getPaymentFailures(reqOptions);

    return dispatch({
      type: LOAD_PAYMENTS_LIST.success,
      data
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: LOAD_PAYMENTS_LIST.failure });
  }
};

export const cancelPaymentAction = (id, closeCallback) => async dispatch => {
  try {
    dispatch({
      type: CANCEL_PAYMENT.start
    });

    await cancelPayment(id);

    closeCallback();

    dispatch(
      showMessage({
        message: 'CANCEL_PAYMENT',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadPayments());

    return dispatch({
      type: CANCEL_PAYMENT.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: CANCEL_PAYMENT.failure });
  }
};

export const editPaymentDueDateAction = (
  id,
  dueDate,
  closeCallback
) => async dispatch => {
  try {
    dispatch({
      type: EDIT_PAYMENT_DUE_DATE.start
    });
    await editPaymentDueDate({ id, dueDate });

    closeCallback();

    dispatch(
      showMessage({
        message: 'EDIT_PAYMENT_DUE_DATE',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadPayments());

    return dispatch({
      type: EDIT_PAYMENT_DUE_DATE.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: EDIT_PAYMENT_DUE_DATE.failure });
  }
};
export const billPaymentAction = id => async dispatch => {
  try {
    dispatch({ type: SEND_PAYMENT_TO_REBILL.start });
    await sendToBillPayment(id);
    dispatch(
      showMessage({
        message: 'PAYMENT_BILLED',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadPayments());

    return dispatch({ type: SEND_PAYMENT_TO_REBILL.success });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: SEND_PAYMENT_TO_REBILL.failure });
  }
};

export const downloadExcelAutomaticDebits = () => async (
  dispatch,
  getState
) => {
  try {
    dispatch({ type: DOWNLOAD_AUTOMATIC_DEBITS.start });

    const reqOptions = getOptions(getState, false);
    await downloadExportedAutomaticDebits(reqOptions);

    return dispatch({ type: DOWNLOAD_AUTOMATIC_DEBITS.success });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: DOWNLOAD_AUTOMATIC_DEBITS.failure });
  }
};

export const generateAutomaticDebitFormatsAction = closeCallback => async dispatch => {
  try {
    dispatch({
      type: GENERATE_AUTOMATIC_DEBIT_FORMATS.start
    });

    await generateAutomaticDebitFormats();

    dispatch(
      showMessage({
        message: 'GENERATE_AUTOMATIC_DEBIT_FORMATS',
        variant: SnackBarVariants.SUCCESS
      })
    );

    closeCallback();

    dispatch(loadPayments());

    return dispatch({
      type: GENERATE_AUTOMATIC_DEBIT_FORMATS.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: GENERATE_AUTOMATIC_DEBIT_FORMATS.failure });
  }
};

export const createAutomaticDebitAction = (
  values,
  closeCallback
) => async dispatch => {
  try {
    dispatch({
      type: CREATE_AUTOMATIC_DEBIT.start
    });

    await createAutomaticDebit(values);

    dispatch(
      showMessage({
        message: 'CREATE_AUTOMATIC_DEBIT',
        variant: SnackBarVariants.SUCCESS
      })
    );

    closeCallback();

    dispatch(loadPayments());

    return dispatch({
      type: CREATE_AUTOMATIC_DEBIT.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: CREATE_AUTOMATIC_DEBIT.failure });
  }
};

export const reactivateAutomaticDebitAction = (
  id,
  dueDate,
  closeCallback
) => async dispatch => {
  try {
    dispatch({
      type: REACTIVATE_AUTOMATIC_DEBIT.start
    });

    await reactivateAutomaticDebit({ id, dueDate });

    dispatch(
      showMessage({
        message: 'REACTIVATE_AUTOMATIC_DEBIT',
        variant: SnackBarVariants.SUCCESS
      })
    );

    closeCallback();

    dispatch(loadPayments());

    return dispatch({
      type: REACTIVATE_AUTOMATIC_DEBIT.success
    });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: REACTIVATE_AUTOMATIC_DEBIT.failure });
  }
};

export const editAutomaticDebitAmountAction = (
  id,
  transactionId,
  amount,
  closeCallback
) => async dispatch => {
  try {
    dispatch({ type: EDIT_AUTOMATIC_DEBIT_AMOUNT.start });

    await editAutomaticDebitAmount({ id, transactionId, amount });

    closeCallback();

    dispatch(
      showMessage({
        message: 'EDIT_AUTOMATIC_DEBIT_AMOUNT',
        variant: SnackBarVariants.SUCCESS
      })
    );

    dispatch(loadPayments());

    return dispatch({ type: EDIT_AUTOMATIC_DEBIT_AMOUNT.success });
  } catch (error) {
    dispatch(
      showMessage({ message: error.message, variant: SnackBarVariants.ERROR })
    );
    return dispatch({ type: EDIT_AUTOMATIC_DEBIT_AMOUNT.failure });
  }
};

export default loadPayments;
